import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";

export type WorkingDay = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export type TenantSettings = {
  id: string | null;
  tenant_id: string;
  display_name: string | null;
  legal_name: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  tax_number: string | null;
  timezone: string;
  working_days: WorkingDay[];
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  logo_url: string | null;
  default_lesson_duration_minutes: number;
  created_at: string | null;
  updated_at: string | null;
};

export type UpdateTenantSettingsInput = {
  displayName: string | null;
  legalName: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  taxNumber: string | null;
  timezone: string;
  workingDays: WorkingDay[];
  brandPrimaryColor: string | null;
  brandSecondaryColor: string | null;
  logoUrl: string | null;
  defaultLessonDurationMinutes: number;
};

export const WORKING_DAY_LABELS: Record<WorkingDay, string> = {
  mon: "Pzt",
  tue: "Sal",
  wed: "Car",
  thu: "Per",
  fri: "Cum",
  sat: "Cmt",
  sun: "Paz",
};

export const ALL_WORKING_DAYS: WorkingDay[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export function useTenantSettings(): {
  data: TenantSettings | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<TenantSettings>("/tenant-settings", undefined, { signal: controller.signal })
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Kurum ayarlari Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { data, loading, error, reload };
}

export async function updateTenantSettings(
  input: UpdateTenantSettingsInput,
): Promise<TenantSettings> {
  return api.put<TenantSettings>("/tenant-settings", input);
}
